import {
    ExtendedAreaModels,
    GeneratedConstructionNumberStatus,
    PropertyTypeStatus,
} from '@bpd-library/utilities';
import { FILTER_NAMES } from '@features/properties/components/property-filter';
import { CONSTRUCTION_NUMBER_STATUS_TRANSLATIONS } from '@source/utilities/api-formatters';
import { hasFilterUrl } from '@source/utilities/filters/urls';
import { translateItem } from '@source/utilities/hooks';

import { Filters } from './construct-filters';

const STATUSES_ORDER: GeneratedConstructionNumberStatus[] = [
    'InPreparation',
    'Available',
    'AssigningOn',
    'InOption',
    'Reserved',
    'Sold',
    'Rented',
    'UnderConstruction',
];

const DEFAULT_STATUSES: GeneratedConstructionNumberStatus[] = [
    'Available',
    'AssigningOn',
    'InOption',
    'InPreparation',
    'Reserved',
    'Sold',
];

const getStatuses = (data: ExtendedAreaModels) => {
    let statuses: GeneratedConstructionNumberStatus[] & PropertyTypeStatus[] = [];

    data.constructionNumbers.forEach((constructionNumber) => {
        const { status } = constructionNumber;

        if (!status || statuses.includes(status)) return;

        statuses.push(status);
    });

    data.propertyTypes.forEach((propertyType) => {
        /*
            When a property type has no construction numbers,
            it will be filtered and in this case the property type
            status has to be included in the filter.
        */
        if (propertyType.constructionNumbers?.length) return;

        // When the status of a property type is SaleSoon, add InPreparation to the filter.
        const status = propertyType.status === 'SaleSoon' ? 'InPreparation' : propertyType.status;

        if (!status || statuses.includes(status)) return;
        if (!(status in CONSTRUCTION_NUMBER_STATUS_TRANSLATIONS)) return;

        statuses.push(status);
    });

    statuses = statuses.sort((a, b) =>
        STATUSES_ORDER.indexOf(a) < STATUSES_ORDER.indexOf(b) ? -1 : 1,
    );

    return statuses;
};

export const setStatusFilter = async (
    data: ExtendedAreaModels,
    filters: Filters,
    options?: { applyDefaultSettings?: boolean; allProjectsSold?: boolean },
) => {
    const statuses = getStatuses(data);
    const filterName = FILTER_NAMES.status;

    if (options?.allProjectsSold) {
        DEFAULT_STATUSES.push('UnderConstruction');
    }

    filters[filterName] = {
        expanded: false,
        icon: 'specs-house',
        title: await translateItem('Filters.Availability.label'),
        dropdownLabel: await translateItem('Filters.Availability.dropdownLabel'),
        filterFunctions: {
            constructionNumber: (constructionNumber, filterInput) =>
                constructionNumber.status === filterInput.value,
            propertyType: (propertyType, filterInput) => {
                if (!propertyType.status) return false;

                /*
                    There will only be filtered on a propertyType when there are no constructionNumbers.
                    In this case, the property type will always be InPreparation
                */

                return filterInput.value === 'InPreparation';
            },
            projectPhase: (projectPhase) => {
                /*
                    The project phases shown on the map are always in preparation.
                */

                return !!projectPhase?.status;
            },
        },
        filterName,
        hasRangeValues: false,
        filters: {
            type: 'checkbox',
            components: await Promise.all(
                statuses.map(async (status) => {
                    let checked = false;

                    if (options?.applyDefaultSettings) {
                        checked = DEFAULT_STATUSES.includes(status);
                    } else {
                        checked = hasFilterUrl(status, FILTER_NAMES.status);
                    }

                    return {
                        id: `${FILTER_NAMES.status}-${status}`,
                        label: await translateItem(CONSTRUCTION_NUMBER_STATUS_TRANSLATIONS[status]),
                        value: status,
                        name: FILTER_NAMES.status,
                        checked,
                        type: 'checkbox',
                    };
                }),
            ),
        },
    };
};
